import React, { useMemo } from 'react';
import { InputBase } from '@material-ui/core';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import { makeStyles } from '@material-ui/core/styles';
import InputLabel from '@material-ui/core/InputLabel';
import ContextualMenu, { ContextualMenuLevelItem } from '../../contextualMenu/ContextualMenu';

export interface FilterSelectItem {
    disabled?: boolean;
    onClick: () => void;
    text: string;
}

const useStyles = makeStyles((theme) => ({
    formControl: {
        padding: theme.spacing(0),
    },
    select: {
        padding: theme.spacing(0),
        height: theme.spacing(30 / 8),
    },
    label: {
        lineHeight: theme.spacing(16 / 8),
        letterSpacing: theme.spacing(0.4 / 8),
        fontSize: theme.spacing(14 / 8),
        top: theme.spacing(8 / 8),
        transform: 'none',
        left: theme.spacing(14 / 8),
    },
    selectInput: {
        transform: 'none',
        borderStyle: 'solid',
        borderWidth: theme.spacing(1 / 8),
        borderRadius: theme.spacing(4 / 8),
    },
    menuItems: {
        display: 'none !important',
    },
}));

interface PropsBasic {
    closeMenuAfterClickToCustomFilter?: boolean;
    closeMenuAfterClickToPredefinedView?: boolean;
    name?: string;
}

interface PropsWithPredefinedViews extends PropsBasic {
    customFiltering?: FilterSelectItem[];
    predefinedViews: FilterSelectItem[];
}

interface PropsWithCustomFiltering extends PropsBasic {
    customFiltering: FilterSelectItem[];
    predefinedViews?: FilterSelectItem[];
}

type Props = PropsWithPredefinedViews | PropsWithCustomFiltering;

const FilterSelect: React.FC<Props> = (props) => {
    const classes = useStyles();

    const [anchorElement, setAnchorElement] = React.useState<HTMLElement | null>(null);

    const {
        closeMenuAfterClickToCustomFilter = true,
        closeMenuAfterClickToPredefinedView = true,
        customFiltering,
        name = 'Add filter',
        predefinedViews,
    } = props;

    const multiLevelList = useMemo((): ContextualMenuLevelItem[] => {
        const list: ContextualMenuLevelItem[] = [];
        if (predefinedViews !== undefined && predefinedViews.length > 0) {
            const predefinedViewsList: ContextualMenuLevelItem = {
                name: 'Predefined views',
                items: (closeMenuAfterClickToPredefinedView === true)
                    ? predefinedViews.map((predefinedView: FilterSelectItem): FilterSelectItem => ({
                        ...predefinedView,
                        onClick: (): void => {
                            setAnchorElement(null);
                            predefinedView.onClick();
                        },
                    }))
                    : predefinedViews,
            };
            list.push(predefinedViewsList);
        }
        if (customFiltering !== undefined && customFiltering.length > 0) {
            const customFilteringList: ContextualMenuLevelItem = {
                name: null,
                items: (closeMenuAfterClickToCustomFilter === true)
                    ? customFiltering.map((predefinedView: FilterSelectItem): FilterSelectItem => ({
                        ...predefinedView,
                        onClick: (): void => {
                            setAnchorElement(null);
                            predefinedView.onClick();
                        },
                    }))
                    : customFiltering,
            };
            list.push(customFilteringList);
        }
        return list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        customFiltering,
        predefinedViews,
    ]);

    const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
        event.stopPropagation();
        setAnchorElement(event.currentTarget);
    };

    const handleClose = (): void => {
        setAnchorElement(null);
    };

    return (
        <>
            <FormControl
                fullWidth
                variant="outlined"
                classes={{
                    root: classes.formControl,
                }}
            >
                <InputLabel
                    shrink={false}
                    classes={{
                        root: classes.label,
                        focused: classes.label,
                    }}
                >
                    {name}
                </InputLabel>
                <Select
                    classes={{
                        root: classes.select,
                    }}
                    open={anchorElement !== null}
                    onClick={handleClick}
                    MenuProps={{
                        classes: {
                            paper: classes.menuItems,
                        },
                    }}
                    input={(
                        <InputBase
                            classes={{
                                root: classes.selectInput,
                            }}
                            value={''}
                        />
                    )}
                />
            </FormControl>
            <ContextualMenu
                anchorElement={anchorElement}
                handleClose={handleClose}
                multiLevelList={multiLevelList}
                anchorOrigin={{
                    vertical: 44,
                    horizontal: 'left',
                }}
                transformOrigin={{
                    horizontal: 'left',
                    vertical: 'top',
                }}
            />
        </>
    );
};

export default FilterSelect;
